import { EntityQueryOrder, getPagingInfo, ok, PublishedEntityQueryOrder, } from '@dossierhq/core';
import isEqual from 'lodash/isEqual.js';
const DEFAULT_VALUES = {
    admin: {
        order: EntityQueryOrder.updatedAt,
        reverse: true,
        requestedCount: 25,
    },
    published: {
        order: PublishedEntityQueryOrder.name,
        reverse: false,
        requestedCount: 25,
    },
};
export function initializeSearchEntityState({ mode, actions, restrictEntityTypes, restrictLinksFrom, restrictLinksTo, }) {
    const defaultValues = DEFAULT_VALUES[mode];
    let state = {
        mode,
        restrictEntityTypes: restrictEntityTypes ?? [],
        restrictLinksFrom: restrictLinksFrom ?? null,
        restrictLinksTo: restrictLinksTo ?? null,
        query: {},
        paging: {},
        sampling: undefined,
        requestedCount: defaultValues.requestedCount,
        text: '',
        connection: undefined,
        connectionError: undefined,
        entitySamples: undefined,
        entitySamplesError: undefined,
        totalCount: null,
        entities: null,
        loadingState: '',
        entitiesScrollToTopSignal: 0,
    };
    // Normalize query state
    state = reduceSearchEntityState(state, new SetQueryAction({}, { partial: true, resetPagingIfModifying: false }));
    if (actions) {
        for (const action of actions) {
            state = reduceSearchEntityState(state, action);
        }
    }
    return state;
}
export function reduceSearchEntityState(state, action) {
    const newState = action.reduce(state);
    // if (state !== newState) console.log(`State changed for ${action.constructor.name}`, state, action, newState);
    return newState;
}
class SetPagingAction {
    paging;
    pagingCause;
    constructor(paging, pagingCause) {
        this.paging = paging;
        this.pagingCause = pagingCause;
    }
    reduce(state) {
        if (isEqual(this.paging, state.paging)) {
            return state;
        }
        const { count } = getPagingInfo(this.paging).valueOrThrow();
        return {
            ...state,
            paging: this.paging,
            sampling: undefined,
            requestedCount: count || state.requestedCount,
            loadingState: this.pagingCause ?? '',
        };
    }
}
class SetSamplingAction {
    value;
    partial;
    constructor(value, partial) {
        this.value = value;
        this.partial = partial;
    }
    reduce(state) {
        const sampling = this.partial ? { ...state.sampling, ...this.value } : { ...this.value };
        if (sampling.seed === undefined) {
            sampling.seed = Math.floor(Math.random() * 999999);
        }
        const query = { ...state.query };
        delete query.order;
        delete query.reverse;
        if (isEqual(sampling, state.sampling) && isEqual(query, state.query)) {
            return state;
        }
        let { loadingState } = state;
        if (sampling) {
            loadingState = 'sample';
        }
        return {
            ...state,
            sampling,
            query,
            paging: undefined,
            requestedCount: sampling.count || state.requestedCount,
            loadingState,
        };
    }
}
class SetQueryAction {
    value;
    partial;
    resetPagingIfModifying;
    constructor(value, { partial, resetPagingIfModifying }) {
        this.value = value;
        this.partial = partial;
        this.resetPagingIfModifying = resetPagingIfModifying;
    }
    reduce(state) {
        const query = this.partial
            ? { ...state.query, ...this.value }
            : { ...this.value };
        // Restrictions
        if (state.restrictEntityTypes.length > 0) {
            if (query.entityTypes && query.entityTypes.length > 0) {
                query.entityTypes = query.entityTypes.filter((it) => state.restrictEntityTypes.includes(it));
            }
            else {
                query.entityTypes = state.restrictEntityTypes;
            }
        }
        if (state.restrictLinksFrom) {
            query.linksFrom = state.restrictLinksFrom;
        }
        if (state.restrictLinksTo) {
            query.linksTo = state.restrictLinksTo;
        }
        // Sampling/paging
        const switchToSearch = (this.value.order || this.value.reverse !== undefined) && state.sampling;
        let { sampling, paging, loadingState } = state;
        if (switchToSearch) {
            sampling = undefined;
            paging = {};
        }
        // Normalize (except for order/reverse)
        if (query.authKeys?.length === 0) {
            delete query.authKeys;
        }
        if (query.entityTypes?.length === 0) {
            delete query.entityTypes;
        }
        if (query.componentTypes?.length === 0) {
            delete query.componentTypes;
        }
        if ('status' in query && query.status?.length === 0) {
            delete query.status;
        }
        if (query.text?.length === 0) {
            delete query.text;
        }
        if (paging) {
            if (!query.order) {
                const defaultValues = DEFAULT_VALUES[state.mode];
                query.order = defaultValues.order;
                query.reverse = defaultValues.reverse;
            }
        }
        else {
            delete query.order;
            delete query.reverse;
        }
        if (isEqual({ query, paging, sampling }, { query: state.query, paging: state.paging, sampling: state.sampling })) {
            return state;
        }
        if (this.resetPagingIfModifying && !sampling) {
            paging = {};
            loadingState = 'first-page';
        }
        return { ...state, query, text: query.text ?? '', paging, sampling, loadingState };
    }
}
class UpdateSearchResultAction {
    connection;
    connectionError;
    constructor(connection, connectionError) {
        this.connection = connection;
        this.connectionError = connectionError;
    }
    reduce(state) {
        if (state.connection === this.connection && state.connectionError === this.connectionError) {
            return state;
        }
        let { entities, entitiesScrollToTopSignal, loadingState } = state;
        if (!state.sampling && this.connection !== undefined) {
            entities = this.connection === null ? [] : this.connection.edges.map((it) => it.node);
            if (['first-page', 'prev-page', 'next-page', 'last-page'].includes(loadingState)) {
                entitiesScrollToTopSignal += 1;
                loadingState = '';
            }
        }
        return {
            ...state,
            connection: this.connection,
            connectionError: this.connectionError,
            entities,
            entitiesScrollToTopSignal,
            loadingState,
        };
    }
}
class UpdateSampleResultAction {
    entitySamples;
    entitySamplesError;
    constructor(entitySamples, entitySamplesError) {
        this.entitySamples = entitySamples;
        this.entitySamplesError = entitySamplesError;
    }
    reduce(state) {
        if (state.entitySamples === this.entitySamples &&
            state.entitySamplesError === this.entitySamplesError) {
            return state;
        }
        let { entities, entitiesScrollToTopSignal, loadingState } = state;
        if (state.sampling && this.entitySamples) {
            entities = this.entitySamples.items.map((it) => ok(it));
            if (loadingState === 'sample') {
                entitiesScrollToTopSignal += 1;
                loadingState = '';
            }
        }
        return {
            ...state,
            entitySamples: this.entitySamples,
            entitySamplesError: this.entitySamplesError,
            entities,
            entitiesScrollToTopSignal,
            loadingState,
        };
    }
}
class UpdateTotalCountAction {
    totalCount;
    constructor(totalCount) {
        this.totalCount = totalCount;
    }
    reduce(state) {
        if (state.totalCount === this.totalCount) {
            return state;
        }
        return {
            ...state,
            totalCount: this.totalCount,
        };
    }
}
export const SearchEntityStateActions = {
    SetPaging: SetPagingAction,
    SetSampling: SetSamplingAction,
    SetQuery: SetQueryAction,
    UpdateSearchResult: UpdateSearchResultAction,
    UpdateSampleResult: UpdateSampleResultAction,
    UpdateTotalCount: UpdateTotalCountAction,
};
export function getQueryWithoutDefaults(mode, query) {
    let changed = false;
    const newQuery = { ...query };
    const defaultValues = DEFAULT_VALUES[mode];
    if (query.order === defaultValues.order && query.reverse === defaultValues.reverse) {
        delete newQuery.order;
        delete newQuery.reverse;
        changed = true;
    }
    if (query.reverse === false) {
        delete newQuery.reverse;
        changed = true;
    }
    return changed ? newQuery : query;
}
//# sourceMappingURL=SearchEntityReducer.js.map